import { add, clamp, dot, finiteVec, headingVector, length, moveToward, rightVector, scale, subtract } from './math'
import type { ControlInput, SurfaceState, Vec2, VehicleKind, VehicleState } from './types'

const NORMAL_TOP_SPEED = 56
const BOOST_TOP_SPEED = 72
const REVERSE_TOP_SPEED = -18
const ACCELERATION = 34
const BOOST_ACCELERATION = 45
const BRAKE_DECELERATION = 38
const REVERSE_ACCELERATION = 15
const COAST_DECELERATION = 5
const NORMAL_TURN_RATE = 1.35
const DRIFT_TURN_RATE = 1.75
const NORMAL_GRIP = 8.5
const DRIFT_GRIP = 3.6
const DRIFT_MIN_SPEED = 16
const DRIFT_MIN_ANGLE = 0.08
const DRIFT_MIN_BANK_DURATION = 0.45
const DRIFT_BANK_MAX = 38
const BOOST_DRAIN_PER_SECOND = 36
export const VEHICLE_CONTACT_DISTANCE = 3.1
const COLLISION_CORRECTION = 0.8
const COLLISION_RESTITUTION = 0.62
const MINIMUM_SEPARATING_SPEED = 1.8
const CONTACT_TANGENTIAL_DAMPING = 0.94
const CONTACT_SPEED_CAP = 76

export const EMPTY_INPUT: ControlInput = {
  throttle: 0,
  brake: 0,
  steer: 0,
  drift: false,
  boost: false,
  fire: false,
  reload: false,
}

export function createVehicle(id: string, kind: VehicleKind, position: Vec2, heading: number): VehicleState {
  return {
    id,
    kind,
    position: { ...position },
    velocity: { x: 0, y: 0 },
    heading,
    speed: 0,
    steer: 0,
    boost: 0,
    boostActive: false,
    drift: {
      active: false,
      angle: 0,
      charge: 0,
      duration: 0,
      longest: 0,
    },
    lastSafePosition: { ...position },
    lastSafeHeading: heading,
    trackSampleIndex: 0,
    ammo: 12,
    reloadTimer: 0,
    weaponCooldown: 0,
    fireSuppressionTimer: 0,
    driverHitCount: 0,
    driverStunTimer: 0,
    hitImmunityTimer: 0,
    engineFatigue: 0,
    offTrack: false,
    respawnCount: 0,
  }
}

export function getCleanCheckpointBoost(input: { lateral: number; trackWidth: number; speed: number }): number {
  return Math.abs(input.lateral) <= input.trackWidth * 0.55 && Math.abs(input.speed) >= 24 ? 6 : 0
}

function recoverInvalidVehicle(vehicle: VehicleState): VehicleState {
  return {
    ...vehicle,
    position: { ...vehicle.lastSafePosition },
    velocity: { x: 0, y: 0 },
    heading: vehicle.lastSafeHeading,
    speed: 0,
    steer: 0,
    boostActive: false,
    drift: { ...vehicle.drift, active: false, angle: 0, charge: 0, duration: 0 },
  }
}

export function stepVehicle(
  vehicle: VehicleState,
  rawInput: ControlInput,
  surface: SurfaceState,
  rawDt: number,
): VehicleState {
  if (!finiteVec(vehicle.position) || !finiteVec(vehicle.velocity) || !Number.isFinite(vehicle.heading)) {
    return recoverInvalidVehicle(vehicle)
  }

  const dt = clamp(rawDt, 0, 0.1)
  const input = {
    ...rawInput,
    throttle: clamp(rawInput.throttle, 0, 1),
    brake: clamp(rawInput.brake, 0, 1),
    steer: clamp(rawInput.steer, -1, 1),
  }
  const previousForward = headingVector(vehicle.heading)
  const previousLongitudinal = dot(vehicle.velocity, previousForward)
  const steer = moveToward(vehicle.steer, input.steer, 5.5 * dt)
  const steeringAuthority = clamp(Math.abs(previousLongitudinal) / 8, 0.08, 1)
  const turnRate = input.drift ? DRIFT_TURN_RATE : NORMAL_TURN_RATE
  const reverseSteering = previousLongitudinal < -0.5 ? -0.55 : 1
  const heading = vehicle.heading + steer * turnRate * steeringAuthority * reverseSteering * dt
  const forward = headingVector(heading)
  const right = rightVector(heading)
  let longitudinal = dot(vehicle.velocity, forward)
  let lateral = dot(vehicle.velocity, right)
  let boost = vehicle.boost
  const boostActive = input.boost && input.throttle > 0 && boost > 0
  const topSpeed = boostActive ? BOOST_TOP_SPEED : NORMAL_TOP_SPEED

  if (input.throttle > 0) {
    const acceleration = boostActive ? BOOST_ACCELERATION : ACCELERATION
    longitudinal = moveToward(longitudinal, topSpeed, acceleration * input.throttle * dt)
  } else if (input.brake > 0) {
    longitudinal =
      longitudinal > 0
        ? moveToward(longitudinal, 0, BRAKE_DECELERATION * input.brake * dt)
        : moveToward(longitudinal, REVERSE_TOP_SPEED, REVERSE_ACCELERATION * input.brake * dt)
  } else {
    longitudinal = moveToward(longitudinal, 0, COAST_DECELERATION * dt)
  }

  longitudinal = clamp(longitudinal, REVERSE_TOP_SPEED, topSpeed)
  if (boostActive) boost = Math.max(0, boost - BOOST_DRAIN_PER_SECOND * dt)
  lateral *= Math.exp(-(input.drift ? DRIFT_GRIP : NORMAL_GRIP) * dt)

  const driftAngle = Math.atan2(lateral, Math.max(0.1, Math.abs(longitudinal)))
  const driftEligible = input.drift && length(vehicle.velocity) >= DRIFT_MIN_SPEED && Math.abs(steer) >= 0.2
  const driftActive = driftEligible && (vehicle.drift.active || Math.abs(driftAngle) >= DRIFT_MIN_ANGLE)
  let driftCharge = vehicle.drift.charge
  let driftDuration = vehicle.drift.duration
  let longest = vehicle.drift.longest

  if (driftActive) {
    driftDuration += dt
    longest = Math.max(longest, driftDuration)
    driftCharge = clamp(driftCharge + Math.abs(driftAngle) * Math.abs(longitudinal) * 1.4 * dt, 0, 100)
  } else if (!input.drift && (driftCharge > 0 || driftDuration > 0)) {
    if (driftDuration >= DRIFT_MIN_BANK_DURATION && driftCharge >= 6) {
      boost = clamp(boost + Math.min(DRIFT_BANK_MAX, driftCharge * 0.72), 0, 100)
    }
    driftCharge = 0
    driftDuration = 0
  }

  const velocity = add(scale(forward, longitudinal), scale(right, lateral))
  const position = add(vehicle.position, scale(velocity, dt))
  const signedSpeed = dot(velocity, forward)
  const safelyOnTrack = Math.abs(surface.lateral) <= surface.trackWidth * 0.78

  return {
    ...vehicle,
    position,
    velocity,
    heading,
    speed: signedSpeed,
    steer,
    boost,
    boostActive,
    drift: {
      active: driftActive,
      angle: driftAngle,
      charge: driftCharge,
      duration: driftDuration,
      longest,
    },
    lastSafePosition: safelyOnTrack ? { ...position } : vehicle.lastSafePosition,
    lastSafeHeading: safelyOnTrack ? heading : vehicle.lastSafeHeading,
  }
}

export function resolveVehicleCollision(a: VehicleState, b: VehicleState): [VehicleState, VehicleState] {
  const offset = subtract(b.position, a.position)
  const currentDistance = length(offset)
  if (currentDistance >= VEHICLE_CONTACT_DISTANCE) return [a, b]

  const normal = currentDistance > 1e-6 ? scale(offset, 1 / currentDistance) : { x: 1, y: 0 }
  const tangent = { x: -normal.y, y: normal.x }
  const correction = scale(normal, (VEHICLE_CONTACT_DISTANCE - currentDistance) * COLLISION_CORRECTION * 0.5)
  const aNormal = dot(a.velocity, normal)
  const bNormal = dot(b.velocity, normal)
  const relativeSpeed = bNormal - aNormal
  const restitutionImpulse = relativeSpeed < 0 ? (-(1 + COLLISION_RESTITUTION) * relativeSpeed) / 2 : 0
  const separationImpulse = Math.max(0, (MINIMUM_SEPARATING_SPEED - relativeSpeed) / 2)
  const impulse = Math.max(restitutionImpulse, separationImpulse)
  const averageTangent = (dot(a.velocity, tangent) + dot(b.velocity, tangent)) * 0.5
  const aTangent = averageTangent + (dot(a.velocity, tangent) - averageTangent) * CONTACT_TANGENTIAL_DAMPING
  const bTangent = averageTangent + (dot(b.velocity, tangent) - averageTangent) * CONTACT_TANGENTIAL_DAMPING

  const capVelocity = (velocity: Vec2): Vec2 => {
    const speed = length(velocity)
    return speed > CONTACT_SPEED_CAP ? scale(velocity, CONTACT_SPEED_CAP / speed) : velocity
  }
  const aVelocity = capVelocity(add(scale(normal, aNormal - impulse), scale(tangent, aTangent)))
  const bVelocity = capVelocity(add(scale(normal, bNormal + impulse), scale(tangent, bTangent)))

  return [
    { ...a, position: subtract(a.position, correction), velocity: aVelocity },
    { ...b, position: add(b.position, correction), velocity: bVelocity },
  ]
}

export function resolveVehiclePackCollisions(vehicles: readonly VehicleState[]): VehicleState[] {
  const next = [...vehicles]
  for (let a = 0; a < next.length; a += 1) {
    for (let b = a + 1; b < next.length; b += 1) {
      const vehicleA = next[a]
      const vehicleB = next[b]
      if (!vehicleA || !vehicleB) continue
      const [resolvedA, resolvedB] = resolveVehicleCollision(vehicleA, vehicleB)
      next[a] = resolvedA
      next[b] = resolvedB
    }
  }
  return next
}
